import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { KINETIC_WORD_SETTING } from './kineticWordSetting';
import { check, repeatsAClass, treatsSeveral } from '../skillUsage';

export const letterMaskRiseSkill: CodeoSkill = {
    name: SKILL_NAMES.letterMaskRise,
    kind: 'codeo',
    description:
        'Bring a word in letter by letter, each glyph rising up out of an invisible slot under its ' +
        'own baseline a frame after the one before, and take it out the same way — the connective ' +
        'entrance and exit of kinetic typography, for a one-word beat that needs to arrive fast ' +
        'and crisp without a fade.',
    checkLooksFor:
        'per-letter spans inside a line with overflow: hidden, tweened on yPercent one after another',
    documentUsesSkill: check(
        (script, markup) =>
            /overflow:\s*hidden|\boverflow-hidden\b/.test(markup) &&
            repeatsAClass(markup, 'span') &&
            /yPercent/.test(script) &&
            treatsSeveral(script),
    ),
    instructions: `# Letter Mask Rise

The word is not faded in. Each letter **rises up out of a slot**: it starts hidden below its own baseline, inside a line that clips everything outside it, and slides up into place. The letters go one after another, a frame or two apart, so the word assembles left to right in about a fifth of a second. To leave, the letters **drop back down through the same slot** in the same order. Nothing changes opacity: a letter is either inside the mask and visible, or below it and gone.

It is the connective tissue of kinetic typography: the ordinary way a one-word beat arrives and leaves, between the beats that act something out. It is fast enough that the word is readable on the frame its sound lands, and clean enough that the eye goes straight to the word.

${KINETIC_WORD_SETTING}

## Markup

One line per word, with \`overflow: hidden\`: the line is the mask. Every letter is its own \`inline-block\` span carrying \`data-l5-allow-clip\`, which tells the clipping check the letter is meant to be cut by the mask while it is outside it. The line carries \`data-l5-allow-literal\`: a word sized to fill the frame is display type, not one of the type scale's sizes. **Set the word in capitals** (\`text-transform: uppercase\`), whatever case the copy is written in: capitals are one even height, so the slot fits them exactly.

\`\`\`html
<div id="beat-1" class="absolute inset-0 flex items-center justify-center">
  <div class="lm-line" data-l5-allow-literal style="overflow: hidden; padding: 0.05em 0.06em 0.08em; line-height: 1; white-space: nowrap; font-family: var(--l5-font-0), sans-serif; font-weight: 800; color: var(--l5-color-3, #fafafa); text-transform: uppercase;">
    <span class="lm-ch" data-l5-allow-clip>S</span><span class="lm-ch" data-l5-allow-clip>H</span><span class="lm-ch" data-l5-allow-clip>I</span><span class="lm-ch" data-l5-allow-clip>P</span>
  </div>
</div>
\`\`\`

\`\`\`css
.lm-ch { display: inline-block; }
\`\`\`

## Timeline code

Size the word to the frame first, so a short word and a long one both fill it: measure the line at a base size, then set the **font-size** so its width is the share of the frame you want. Never stretch the line with \`scaleX\` to fit: that distorts the letters. Then each letter rises on \`yPercent\`, which is its own height, so a letter parked at 160 is wholly out of the mask whatever the font size, even while it is stretched.

\`\`\`js
const tl = gsap.timeline({ paused: true });
const CUE_IN = 0.4, CUE_OUT = 1.6;   // the word's cue, and the next beat's
const STAGGER = 0.025;               // 1–2 frames between letters
const line = document.querySelector('#beat-1 .lm-line');
fitWord(line, 0.82);
const letters = line.querySelectorAll('.lm-ch');

// In: up out of the slot, stretched while it moves, square when it lands.
letters.forEach((ch, i) => {
  tl.fromTo(ch,
    { yPercent: 160, scaleY: 1.35, transformOrigin: '50% 100%' },
    { yPercent: 0, scaleY: 1, duration: 0.22, ease: 'power3.out' },
    CUE_IN - 0.08 + i * STAGGER);
});

// Out: down through the same slot, accelerating, finished before the next word lands.
letters.forEach((ch, i) => {
  tl.to(ch, { yPercent: 160, scaleY: 1.25, duration: 0.16, ease: 'power2.in' },
    CUE_OUT - 0.2 + i * STAGGER);
});

tl.set({}, {}, 2.0);
window.__timelines = { main: tl };
\`\`\`

## Adapt

- **A stack of words.** Give each line its own mask and start each line 2–4 letters' worth after the one above, so the stack builds down the frame like one sentence being set.
- **Falling instead of rising.** A word about falling, dropping or sinking comes in from above (\`yPercent: -160\`, stretched from its top) and leaves downward.
- **Hand off in the same slot.** For a word replaced by the next one in place, start the outgoing drop two frames before the incoming rise in the same line: the second word rises as the first falls away.
- **Order.** Left to right is the default; a word about meeting or closing in can rise outside-in.
- **Size.** One word on its own takes 75–90% of the frame width. Two stacked words share a width and set their sizes so both fill it.

## Pitfalls

- **Park it deep enough.** A stretched letter is taller than its slot, so a letter parked just below the mask shows its top as a sliver before it moves. Park at \`yPercent\` 160 or beyond, and look at the first frame: the line must be empty.
- **No opacity.** A letter that fades as it rises is a different, softer effect. The mask does the hiding; the letters stay at full opacity throughout.
- **The mask must clip.** \`overflow: hidden\` goes on the line, not on each letter and not on the frame. Without it the letters slide up from below the frame and the word reads as falling into place from nowhere.
- **Keep the mask tight.** The padding is a hair of clearance (about 0.05–0.1em), no more. Padding the line generously to stop the clipping check from complaining leaves the parked letters visible inside the mask, so the word is on screen before it arrives. The \`data-l5-allow-clip\` on each letter is what answers that check.
- **Fast, not slow.** Each letter takes 0.15–0.25s. Slowed to fill time, the rise turns into a crawl. Hold the landed word instead.
- The letters are transform-tweened, so they carry no CSS \`transform\`.`,
};
